import { TableCell, TableRow, Tooltip } from '@mui/material';
import { FC, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MarketOrderType } from '../../../app/services/api/orderManagementClient';
import { formatCutoffTimeForDisplay } from '../../../utils/timeUtils';
import { UserContext } from '../../../components/shared/useUser';

type Props = {
  orderType: MarketOrderType;
};

const OrderTypeItemRow: FC<Props> = ({ orderType }) => {
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const [searchParams] = useSearchParams();

  const canEdit = hasPermissionTo(['ConfigurationAdmin']) || hasPermissionTo(['OrderTypeUpdate']);

  const handleRowClick = () => {
    if (!canEdit) return;

    const params = new URLSearchParams();
    const companyId = searchParams.get('companyId');
    const customerTypeId = searchParams.get('customerTypeId');
    const searchTerm = searchParams.get('searchTerm');

    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    navigate(`/order-types/edit/${orderType.companyOrderTypeId}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatTime = (time: string | undefined) => {
    return formatCutoffTimeForDisplay(time);
  };

  return (
    <TableRow
      onClick={canEdit ? handleRowClick : undefined}
      sx={{
        cursor: canEdit ? 'pointer' : 'default',
        '&:hover': canEdit ? {
          backgroundColor: 'action.hover',
        } : undefined,
      }}
    >
      <TableCell>
        {orderType.orderTypeId}
      </TableCell>
      <TableCell>
        <Tooltip 
          title={orderType.name} 
          placement="top"
        >
          <span>{truncateText(orderType.name)}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={orderType.marketName || 'All Markets'} 
          placement="top"
        >
          <span>{truncateText(orderType.marketName || 'All Markets')}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Tooltip 
          title={orderType.instanceName} 
          placement="top"
        >
          <span>{truncateText(orderType.instanceName)}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {formatTime(orderType.cutoff?.orderCutOffTime)}
      </TableCell>
    </TableRow>
  );
};

export default OrderTypeItemRow;
